// @ts-nocheck
import type React from 'react';
import type { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import type { FieldConfig } from './types';

export type ZodObjectOrWrapped = z.ZodTypeAny;

/**
 * Beautify a camelCase string.
 * e.g. "myString" -> "My String"
 */
export function beautifyObjectName(string: string) {
  // if numbers only return the string
  let output = string.replace(/([A-Z])/g, ' $1');
  output = output.charAt(0).toUpperCase() + output.slice(1);
  return output;
}

/**
 * Get the lowest level Zod type.
 * This will unpack optionals, refinements, etc.
 */
export function getBaseSchema<ChildType extends z.ZodTypeAny = z.ZodTypeAny>(
  schema?: z.ZodTypeAny | null
): ChildType | null {
  if (!schema) return null;
  const def = (schema as any)._def;

  if (
    schema instanceof z.ZodOptional ||
    schema instanceof z.ZodNullable ||
    schema instanceof z.ZodDefault ||
    schema instanceof z.ZodCatch ||
    schema instanceof z.ZodReadonly
  ) {
    const inner = def?.innerType as z.ZodTypeAny;
    return getBaseSchema(inner) as ChildType;
  }

  if (def?.type === 'pipe' && def?.in) {
    return getBaseSchema(def.in) as ChildType;
  }

  return schema as ChildType;
}

/**
 * Get the type name of the lowest level Zod type.
 * This will unpack optionals, refinements, etc.
 */
export function getBaseType(schema: z.ZodAny): string {
  const baseSchema = getBaseSchema(schema);
  return (baseSchema?._def?.type as string) || '';
}

/**
 * Search for a "ZodDefult" in the Zod stack and return its value.
 */
export function getDefaultValueInZodStack(schema: z.ZodAny): any {
  if (schema instanceof z.ZodDefault) {
    return schema._def.defaultValue();
  }

  const def = (schema as any)._def;
  if (def?.innerType) {
    return getDefaultValueInZodStack(def.innerType as z.ZodAny);
  }
  if (def?.schema) {
    return getDefaultValueInZodStack(def.schema as z.ZodAny);
  }

  return undefined;
}

/**
 * Get all default values from a Zod schema.
 */
export function getDefaultValues<Schema extends z.ZodObject<any, any>>(
  schema: Schema,
  fieldConfig?: FieldConfig<z.infer<Schema>>
) {
  if (!schema) return null;
  const { shape } = schema;
  type DefaultValuesType = DefaultValues<Partial<z.infer<Schema>>>;
  const defaultValues = {} as DefaultValuesType;
  if (!shape) return defaultValues;

  for (const key of Object.keys(shape)) {
    const item = shape[key] as z.ZodAny;

    const baseSchema = getBaseSchema(item);
    if (baseSchema instanceof z.ZodObject) {
      const defaultItems = getDefaultValues(
        baseSchema as unknown as z.ZodObject<any, any>,
        fieldConfig?.[key] as FieldConfig<z.infer<Schema>>
      );

      if (defaultItems !== null) {
        for (const defaultItemKey of Object.keys(defaultItems)) {
          const pathKey = `${key}.${defaultItemKey}` as keyof DefaultValuesType;
          defaultValues[pathKey] = defaultItems[defaultItemKey];
        }
      }
    } else {
      let defaultValue = getDefaultValueInZodStack(item);
      if ((defaultValue === null || defaultValue === '') && fieldConfig?.[key]?.inputProps) {
        defaultValue = (fieldConfig?.[key]?.inputProps as unknown as any).defaultValue;
      }
      if (defaultValue !== undefined) {
        defaultValues[key as keyof DefaultValuesType] = defaultValue;
      }
    }
  }

  return defaultValues;
}

export function getObjectFormSchema(schema: ZodObjectOrWrapped): z.ZodObject<any> {
  const base = getBaseSchema(schema);
  if (base instanceof z.ZodObject) {
    return base;
  }
  throw new Error('Expected ZodObject schema');
}

/**
 * Convert a Zod schema to HTML input props to give direct feedback to the user.
 * Once submitted, the schema will be validated completely.
 */
export function zodToHtmlInputProps(
  schema: z.ZodTypeAny
): React.InputHTMLAttributes<HTMLInputElement> {
  const isOptional = typeof schema.isOptional === 'function' ? schema.isOptional() : false;
  return {
    required: !isOptional,
  };
}
